import { useMemo, useState } from "react";
import { canonicalizeChatModelId, useChatModels, WORKSHOP_AUTO_MODEL_ID } from "@/hooks/useChatModels";
import { useWorkshopModelStore } from "@/stores/workshopModelStore";
import type { ChatModel, ReasoningEffort } from "@/types/chat.type";

/** The efforts the Workshop API accepts (never Minimal). */
const WORKSHOP_EFFORTS = new Set<ReasoningEffort>(["low", "medium", "high", "xhigh", "max"]);

function defaultEffort(model: ChatModel | null): ReasoningEffort {
  const opts = model?.reasoningOptions;
  if (opts?.includes("high")) return "high";
  return opts?.[0] ?? "high";
}

/**
 * The model and effort a chat starts with: the person's last Workshop pick,
 * else Auto. Changing it here is remembered for the next chat too.
 */
export function useWorkshopModelPick() {
  const { data: rawModels = [] } = useChatModels({ autoModelId: WORKSHOP_AUTO_MODEL_ID });
  const models = useMemo(
    () =>
      rawModels.map((m) => ({
        ...m,
        reasoningOptions: m.reasoningOptions?.filter((o) => WORKSHOP_EFFORTS.has(o)) ?? null,
      })),
    [rawModels],
  );
  const storedModelId = useWorkshopModelStore((s) => s.modelId);
  const storedEffort = useWorkshopModelStore((s) => s.reasoningEffort);
  const setStoredSelection = useWorkshopModelStore((s) => s.setSelection);
  const [modelOverride, setModelOverride] = useState<ChatModel | null>(null);
  const [effortOverride, setEffortOverride] = useState<ReasoningEffort | null>(null);

  const canonicalStoredId = storedModelId ? canonicalizeChatModelId(storedModelId) : null;
  const preferredModel =
    (canonicalStoredId ? models.find((m) => m.id === canonicalStoredId && !m.disabled) : undefined) ??
    models.find((m) => m.id === "auto" && !m.disabled) ??
    models.find((m) => !m.disabled) ??
    models[0] ??
    null;
  const model = modelOverride ?? preferredModel;
  const storedPreferredEffort =
    !modelOverride && model && model.id === canonicalStoredId ? storedEffort : null;
  const effort =
    effortOverride && model?.reasoningOptions?.includes(effortOverride)
      ? effortOverride
      : storedPreferredEffort && model?.reasoningOptions?.includes(storedPreferredEffort)
        ? storedPreferredEffort
        : defaultEffort(model);

  return {
    models,
    model,
    effort,
    setModel: (next: ChatModel) => {
      const nextEffort = defaultEffort(next);
      setModelOverride(next);
      setEffortOverride(nextEffort);
      setStoredSelection(next.id, nextEffort);
    },
    setEffort: (next: ReasoningEffort) => {
      setEffortOverride(next);
      if (model) setStoredSelection(model.id, next);
    },
  };
}
